<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    :title="`ルール${isEdit ? '編集' : '追加'}`"
    :sub-title="`イントラネットFW プラットフォームゲートウェイ ${srcZone} → ${
      srcZone === 'WVS2' ? 'KCPS' : 'WVS2'
    }`"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- ID -->
                <v-row>
                  <v-col>
                    <template v-if="isEdit">
                      <app-label-group label="ID" :value="form.policyId" />
                    </template>
                    <template v-else>
                      <validation-provider
                        name="policyId"
                        v-model="form.policyId"
                        label="ID"
                        :rules="{
                          required: true,
                          numeric: true,
                          between: { min: 1001, max: 9999 },
                          is_not_zero_padding: true,
                          duplicate: { list: existsPolicyIds },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="ID"
                          :msg="$filter.message('info_policy_id')"
                          placement="end"
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </template>
                  </v-col>
                </v-row>
                <!-- 送信元 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="srcAddressList"
                        v-model="form.srcAddressList"
                        label="送信元アドレス"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage }"
                      >
                        <app-badge
                          item="送信元アドレス"
                          :is-info="false"
                          :isRequired="true"
                        ></app-badge>
                        <app-multi-select
                          v-bind="componentField"
                          :options="srcAddressOptions"
                          :multiple="true"
                          label="ipAddress"
                          track-by="ipAddressId"
                          :sort-orders="[
                            ['ipAddressName', 'asc'],
                            ['ipAddress', 'asc'],
                          ]"
                          group-label="label"
                          group-values="values"
                          :closeOnSelect="false"
                          :group-select="true"
                          selectGroupLabel="全てを選択します"
                          deselectGroupLabel="全ての選択を取り消します"
                          :error-messages="errorMessage"
                        >
                        </app-multi-select>
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 宛先 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="dstAddressList"
                        v-model="form.dstAddressList"
                        label="宛先アドレス"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage }"
                      >
                        <app-badge
                          item="宛先アドレス"
                          :is-info="false"
                          :isRequired="true"
                        ></app-badge>
                        <app-multi-select
                          v-bind="componentField"
                          :options="dstAddressOptions"
                          :multiple="true"
                          label="ipAddress"
                          track-by="ipAddressId"
                          :sort-orders="[
                            ['ipAddressName', 'asc'],
                            ['ipAddress', 'asc'],
                          ]"
                          group-label="label"
                          group-values="values"
                          :closeOnSelect="false"
                          :group-select="true"
                          selectGroupLabel="全てを選択します"
                          deselectGroupLabel="全ての選択を取り消します"
                          :error-messages="errorMessage"
                        >
                        </app-multi-select>
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>

                <!-- サービス -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <app-badge
                        item="サービス"
                        msg="サービスを選択する場合は、１つ以上指定してください。"
                        placement="end"
                        :isRequired="serviceOption === 'SELECT'"
                      />
                      <v-radio-group v-model="serviceOption" density="compact">
                        <v-radio value="ANY" label="any" />
                        <v-radio value="SELECT" label="Select" />
                      </v-radio-group>
                      <template v-if="serviceOption === 'SELECT'">
                        <validation-provider
                          name="serviceList"
                          v-model="form.serviceList"
                          label="定義済みサービスおよび個別サービス一覧"
                          :rules="{ required: true }"
                          v-slot="{ componentField, errorMessage }"
                        >
                          <app-badge
                            item="定義済みサービスおよび個別サービス一覧"
                            :is-info="false"
                            :isRequired="false"
                          />
                          <app-multi-select
                            v-bind="componentField"
                            :options="serviceOptions"
                            :multiple="true"
                            :sort-orders="[
                              ['_sortKey', 'asc'],
                              ['serviceName', 'asc'],
                            ]"
                            group-label="label"
                            group-values="values"
                            :custom-label="serviceNameLabel"
                            track-by="_id"
                            :closeOnSelect="false"
                            :group-select="true"
                            selectGroupLabel="グループ全てを選択します"
                            deselectGroupLabel="グループ全ての選択を取り消します"
                            :error-messages="errorMessage"
                          >
                            <template #tag="{ option, remove }">
                              <multi-select-tag
                                :option="option"
                                :remove="remove"
                              >
                                <div class="multiselect-tbody">
                                  <p class="v-col-5 pa-0">
                                    {{ option.serviceName }}
                                  </p>
                                  <p class="v-col-1 pa-0">
                                    {{ option.protocol }}
                                  </p>
                                  <template v-if="option.protocol === 'ICMP'">
                                    <p>
                                      {{
                                        `${option.icmpType}(${option.icmpCode})`
                                      }}
                                    </p>
                                  </template>
                                  <template
                                    v-else-if="option.protocol === 'IP'"
                                  >
                                    <p>{{ option.protocolNumber }}</p>
                                  </template>
                                  <template v-else>
                                    <p>{{ option.portNumber }}</p>
                                  </template>
                                </div>
                              </multi-select-tag>
                            </template>

                            <template #option="{ option }">
                              <template v-if="option.$isLabel">
                                {{ option.$groupLabel }}
                              </template>
                              <div class="multiselect-tbody">
                                <p class="v-col-5 pa-0">
                                  {{ option.serviceName }}
                                </p>
                                <p class="v-col-1 pa-0">
                                  {{ option.protocol }}
                                </p>
                                <template v-if="option.protocol === 'ICMP'">
                                  <p class="v-col-5 pa-0">
                                    {{
                                      `${option.icmpType}(${option.icmpCode})`
                                    }}
                                  </p>
                                </template>
                                <template v-else-if="option.protocol === 'IP'">
                                  <p class="v-col-5 pa-0">
                                    {{ option.protocolNumber }}
                                  </p>
                                </template>
                                <template v-else>
                                  <p class="v-col-5 pa-0">
                                    {{ option.portNumber }}
                                  </p>
                                </template>
                              </div>
                            </template>
                          </app-multi-select>
                        </validation-provider>
                      </template>
                    </v-sheet>
                  </v-col>
                </v-row>

                <!-- アクション・有効/無効 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <v-row>
                        <v-col>
                          <v-row>
                            <v-col cols="4" class="pt-2">
                              <app-badge
                                item="アクション"
                                :isInfo="false"
                                :isRequired="false"
                              />
                            </v-col>
                            <v-col>
                              <v-radio-group
                                v-model="form.actionType"
                                density="comfortable"
                                hide-details
                              >
                                <v-radio value="ALLOW" label="Allow" />
                                <v-radio value="DENY" label="Deny" />
                              </v-radio-group>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col>
                          <v-row>
                            <v-col cols="4" class="pt-2">
                              <app-badge
                                item="有効/無効"
                                :isInfo="false"
                                :isRequired="false"
                              />
                            </v-col>
                            <v-col>
                              <v-radio-group
                                v-model="form.isPolicyStatus"
                                density="comfortable"
                                hide-details
                              >
                                <v-radio :value="true" label="有効" />
                                <v-radio :value="false" label="無効" />
                              </v-radio-group>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </v-col>
                </v-row>

                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="description"
                        v-model="form.description"
                        label="備考"
                        :rules="{ max: { length: 20 } }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="備考"
                          :is-required="false"
                          :msg="
                            $filter.message('info_max_length', {
                              maxLength: 20,
                            })
                          "
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">保存</v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import cloneDeep from "lodash/cloneDeep";
import type { DefaultServiceIntranetfw } from "@/apis/ServiceApi";
import type {
  CustomServiceIntranet,
  CustomServiceIntranetfwList,
} from "@/apis/CustomServiceApi";
import MultiSelectTag from "@/components/MultiSelectTag.vue";
import type { IntraPolicy } from "@/apis/IntranetfwPolicyApi";
import IntranetFwPolicyRuleConfirm from "@/modals/applianceContractSetting/IntranetFirewall/policy/ifw/IntranetFwPolicyRuleConfirm.vue";
import type { PfgwZone } from "@/apis/PfgwZoneApi";
import type { PfgwZoneType } from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyZoneList.vue";
import { VueFinalModal } from "vue-final-modal";

// デフォルト/カスタムを識別するためのIDを付与した型
type EditDefaultService = DefaultServiceIntranetfw["defaultServiceList"][0] & {
  _id: string;
};
type EditCustomService = CustomServiceIntranet & {
  _id: string;
};

/** ルール編集のフォーム用に定義した型 */
type EditIntranetFwPolicy = Omit<
  IntraPolicy,
  "serviceList" | "customServiceList" | "policyObjectId"
> & {
  /** ポリシーオブジェクトID そのままだとnull許容しないので再度定義 */
  policyObjectId: string | null;
  /** デフォルトサービスリスト & カスタムサービスリスト */
  serviceList: (EditDefaultService | EditCustomService)[];
};

export default defineComponent({
  name: "IntranetFwPfgwPolicyRuleEdit",
  components: { MultiSelectTag },
  props: {
    /** 送信元ゾーン */
    srcZone: {
      type: String as PropType<PfgwZoneType>,
      required: true,
    },
    /** 変更対象ポリシー 新規作成の場合は不要 */
    policy: {
      type: Object as PropType<IntraPolicy | undefined>,
      required: false,
      default: undefined,
    },
    /** E番号(ACT) */
    enumberAct: {
      type: String as PropType<string>,
      required: true,
    },
    /** ポリシー全体 ポリシーIDの重複チェックで必要 変更時は不要 */
    allPolicy: {
      type: Array as PropType<IntraPolicy[] | undefined>,
      required: false,
      default: undefined,
    },
  },
  data() {
    const clonePolicy = cloneDeep(this.policy);
    const form = (
      clonePolicy
        ? // 更新
          {
            ...clonePolicy,
            serviceList: [
              ...clonePolicy.customServiceList.map((e) => ({
                _id: `custom_${e.customServiceSeq}`,
                serviceName: e.customServiceName,
                _sortKey: "custom",
                ...e,
              })),
              ...clonePolicy.serviceList.map((e) => ({
                _id: `default_${e.serviceSeq}`,
                _sortKey: "default",
                ...e,
              })),
            ],
          }
        : // 新規
          {
            policyObjectId: null,
            policyId: "",
            srcAddressList: [],
            dstAddressList: [],
            serviceList: [],
            actionType: "ALLOW",
            isPolicyStatus: true,
            description: "",
            policyCategoryInfo: "CUSTOM_POLICY",
          }
    ) as EditIntranetFwPolicy;
    return {
      form: form,
      // 送信元、宛先（any/アドレス）
      srcAddresses: [] as
        | PfgwZone["wvs2AddressList"]
        | PfgwZone["kcpsAddressList"],
      dstAddresses: [] as
        | PfgwZone["wvs2AddressList"]
        | PfgwZone["kcpsAddressList"],
      // デフォルトサービス,カスタムサービス
      defaultServices: [] as DefaultServiceIntranetfw["defaultServiceList"],
      customServices:
        [] as CustomServiceIntranetfwList["customServiceIntranetList"],
      serviceOption: (!clonePolicy || form.serviceList.length === 0
        ? "ANY"
        : "SELECT") as "ANY" | "SELECT",
      isLoaded: false,
    };
  },
  computed: {
    /** true: 編集, false: 新規 */
    isEdit(): boolean {
      return !!this.policy;
    },
    /** 送信元アドレスのSelectの選択肢 */
    srcAddressOptions(): {
      label: string;
      values: PfgwZone["wvs2AddressList"] | PfgwZone["kcpsAddressList"];
    }[] {
      return this.srcAddresses.length !== 0
        ? [
            {
              label: "全選択",
              values: this.srcAddresses,
            },
          ]
        : [];
    },
    /** 宛先アドレスのSelectの選択肢 */
    dstAddressOptions(): {
      label: string;
      values: PfgwZone["wvs2AddressList"] | PfgwZone["kcpsAddressList"];
    }[] {
      return this.dstAddresses.length !== 0
        ? [
            {
              label: "全選択",
              values: this.dstAddresses,
            },
          ]
        : [];
    },
    /** デフォルトサービス/個別サービスSelectの選択肢 */
    serviceOptions(): {
      label: string;
      values: EditDefaultService[] | EditCustomService[];
    }[] {
      const options = [];
      // 個別サービス
      if (this.customServices.length !== 0) {
        options.push({
          label: "個別サービス（全選択）",
          values: this.customServices.map((e) => ({
            _id: `custom_${e.customServiceSeq}`,
            ...e,
          })),
        });
      }
      // デフォルトサービス
      if (this.defaultServices.length !== 0) {
        options.push({
          label: "定義済みサービス（全選択）",
          values: this.defaultServices.map((e) => ({
            _id: `default_${e.serviceSeq}`,
            ...e,
          })),
        });
      }

      return options;
    },
    /** 既存のポリシーID */
    existsPolicyIds(): string[] {
      // 既存のポリシーに同じポリシーIDが存在した場合はエラーとする
      return this.allPolicy!.map((v) => v.policyId);
    },
  },
  async mounted() {
    // 送信元アドレス・宛先アドレスの取得
    await this.getPfgwAddress();
    // デフォルト・カスタムサービスの取得
    await this.getServiceList();
    this.isLoaded = true;
  },
  methods: {
    /** PFGWのアドレス情報を取得 */
    async getPfgwAddress() {
      // PFGWゾーンAPIからアドレス情報を取得する
      const { wvs2AddressList, kcpsAddressList } =
        await this.$api.pfgwZoneRelation.getPfgwZone(this.enumberAct);

      if (this.srcZone === "WVS2") {
        // 送信元ゾーンがWVS2の場合は、送信元アドレスリストにWVS2アドレス、宛先アドレスリストにKCPSのアドレスを設定
        this.srcAddresses = wvs2AddressList.sortBy(["ipAddress", "asc"]);
        this.dstAddresses = kcpsAddressList.sortBy(["ipAddress", "asc"]);
      } else {
        // 送信元ゾーンがKCPSの場合は、送信元アドレスリストにKCPSアドレス、宛先アドレスリストにWVS2のアドレスを設定
        this.srcAddresses = kcpsAddressList.sortBy(["ipAddress", "asc"]);
        this.dstAddresses = wvs2AddressList.sortBy(["ipAddress", "asc"]);
      }
    },

    /** デフォルト・カスタムサービスリストの取得 */
    async getServiceList() {
      // デフォルトサービスの取得 サービス名の昇順にソート
      this.defaultServices = (
        await this.$api.serviceApi.getDefaultServiceIntranetfw()
      ).defaultServiceList.sortBy(["serviceName", "asc"]);
      // カスタムサービスの取得 サービス名の昇順にソート
      this.customServices = (
        await this.$api.customServiceApi.getIntranetfwCustomService(
          "INTRANET_FW_PFGW",
          this.enumberAct
        )
      ).customServiceIntranetList.sortBy(["serviceName", "asc"]);
    },

    /** ルール追加・編集の完了 */
    async submit() {
      // 選択したデータのバックアップ
      const bkSrcAddresses = this.form.srcAddressList;
      const bkDstAddresses = this.form.dstAddressList;
      const bkServiceList = this.form.serviceList;

      // anyを選択している場合にリストをクリアする
      if (this.serviceOption === "ANY") this.form.serviceList = [];

      // 更新用にデータを整形
      const policy = {
        ...this.form,
        // デフォルト、カスタムサービスリストを分解
        serviceList: this.form.serviceList.filter((e) => "serviceSeq" in e),
        customServiceList: this.form.serviceList.filter(
          (e) => "customServiceSeq" in e
        ),
      };

      // anyを選択して確認画面でキャンセルしても、選択した値が保持されるようにバックアップした値から復元する
      this.form.srcAddressList = bkSrcAddresses;
      this.form.dstAddressList = bkDstAddresses;
      this.form.serviceList = bkServiceList;

      // 確認モーダルの表示
      await this.$modal.show(IntranetFwPolicyRuleConfirm, {
        policy: policy,
      });

      // モーダルを閉じて編集したデータを遷移元に渡す
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(policy);
    },

    /** サービスラベル名（検索用） */
    serviceNameLabel(option: EditCustomService | EditDefaultService): string {
      if (option.protocol === "IP") {
        return `${option.serviceName} ${option.protocol} ${option.protocolNumber}`;
      } else if (option.protocol === "ICMP") {
        return `${option.serviceName} ${option.protocol} ${option.icmpType}(${option.icmpCode})`;
      } else {
        return `${option.serviceName} ${option.protocol} ${option.portNumber}`;
      }
    },
  },
});
</script>

<style scoped lang="scss">
:deep(.multiselect__tag) {
  display: block;
}
</style>
